/**
 * Shared multi-model fallback for the AI scanners (label, equipment,
 * and the upcoming workout scanner).
 *
 * In multi-model mode a scan request can be served by any of several
 * AI models. When one model's provider fails, we retry the scan with a
 * different model instead of failing the whole scan. Models that
 * reported a provider-side failure go on a one-hour blacklist so the
 * next scans skip them. The try order is shuffled by default so no single
 * model gets all the traffic; a scanner can instead keep the configured
 * order (`ModelTryOrder.AsConfigured`), which the meal scanner does because
 * its models differ in how well they estimate grams (#6810).
 *
 * The retry/blacklist rules are the converged ones from the scanner
 * refactor (design-docs/scanner-shared-components.md, decision #19):
 * - Blacklist + try next model ONLY on provider-side errors
 *   (provider_error / provider_timeout reported by the server).
 * - A thrown network error retries the next model but does NOT
 *   blacklist - it is not the model's fault.
 * - Any other server-reported error (bad image, auth, quota...) is
 *   returned immediately - retrying another model cannot help.
 * - When every model is blacklisted, the blacklist is cleared and all
 *   models become candidates again.
 */

/** The order in which a multi-model scan tries its models. */
export enum ModelTryOrder {
  /** A fair random order per scan, so no single model gets all the traffic. */
  Shuffled = 'shuffled',
  /** The order the config lists them in: the first model serves every scan
   *  it can, the rest are backups. */
  AsConfigured = 'asConfigured',
}

/** How long a model stays blacklisted after a provider-side failure. */
export const MULTI_MODEL_BLACKLIST_TTL_MS = 60 * 60 * 1000;

/**
 * Holds the per-page model blacklist. Lives as long as the page does,
 * so a blacklisted model stays skipped across scans in the same visit.
 */
export class MultiModelFallbackManager {
  private blacklist = new Map<string, number>();  // modelKey -> expiry timestamp

  constructor(private readonly cooldownMs: number = MULTI_MODEL_BLACKLIST_TTL_MS) {}

  /** Models that are not currently blacklisted. If every model is
   *  blacklisted, clears the blacklist and returns all of them. */
  availableModels(models: string[]): string[] {
    const now = Date.now();
    const available = models.filter(key => {
      const expiry = this.blacklist.get(key);
      return !expiry || now >= expiry;
    });
    if (available.length > 0) return available;
    // all blacklisted - clear and try all again
    this.blacklist.clear();
    return [...models];
  }

  blacklistModel(key: string) {
    this.blacklist.set(key, Date.now() + this.cooldownMs);
  }

  /** Available models in a fair random order (Fisher-Yates shuffle).
   *  `random` is injectable for deterministic tests. */
  shuffledCandidates(models: string[], random: () => number = Math.random): string[] {
    const order = this.availableModels(models);
    for (let i = order.length - 1; i > 0; i--) {
      const j = Math.floor(random() * (i + 1));
      [order[i], order[j]] = [order[j], order[i]];
    }
    return order;
  }
}

export interface MultiModelScanOptions<TResult> {
  /** The configured multi-mode model keys. */
  models: string[];
  /** The page's blacklist holder (kept across scans in one visit). */
  manager: MultiModelFallbackManager;
  /** Performs one scan attempt. The page puts its per-attempt
   *  telemetry/logging here. `attempt` is 0-based, `total` is the
   *  number of candidates this run. */
  scanAsync: (modelKey: string, attempt: number, total: number) => Promise<TResult>;
  /** True when the result carries a provider-side error
   *  (provider_error / provider_timeout) - blacklist + try next. */
  isProviderSideError: (result: TResult) => boolean;
  /** Builds the result returned when a request threw or we are
   *  offline. `modelKey` is null for the offline pre-check. */
  makeNetworkErrorResult: (modelKey: string | null, err: unknown) => TResult;
  /** The page's destroy check - when true, the run returns null. */
  isAborted?: () => boolean;
  /** Called before retrying with the next model (show retry feedback). */
  onRetry?: () => void;
  /** Logging hook for a thrown request error (no blacklist). */
  onNetworkError?: (modelKey: string, err: unknown) => void;
  /** Logging hook for a provider-side error (model gets blacklisted). */
  onProviderError?: (modelKey: string, result: TResult) => void;
  /** The connectivity gate. This REFUSES to scan when it returns false, so callers pass the
   * re-checking form — `() => this.onlineState.isLikelyOnlineOrRecheckAsync()` — not the cheap
   * cached read. A single failed probe can leave the cached connection state stuck on Offline on a
   * device that is working fine, and we would then refuse every scan (and every retry, which
   * re-reads the same stale value) for as long as it stayed stuck. The six-minute foreground tick
   * does re-check an Offline belief (df-eabc7656368c), but six minutes of refusing every scan is
   * not an answer.
   * A gate that blocks the user must be sure. Tests pass a plain boolean stub.
   *
   * Required on purpose (F9): there is no navigator.onLine default any more, because that flag is
   * unreliable — `true` only means "some network interface is up", not "our server is reachable". */
  isOnline: () => boolean | Promise<boolean>;
  /** The order the models are tried in; defaults to `ModelTryOrder.Shuffled`. */
  tryOrder?: ModelTryOrder;
  /** Injectable for tests; defaults to Math.random. */
  random?: () => number;
}

/**
 * Runs a scan with sequential model fallback. Returns the first
 * non-provider-error result, the last failure when every candidate
 * failed, or null when `isAborted` reported the page went away.
 */
export async function runMultiModelScanAsync<TResult>(options: MultiModelScanOptions<TResult>): Promise<TResult | null> {
  const order = options.tryOrder === ModelTryOrder.AsConfigured
    ? options.manager.availableModels(options.models)
    : options.manager.shuffledCandidates(options.models, options.random ?? (() => Math.random()));
  let lastResult: TResult | null = null;

  for (let attempt = 0; attempt < order.length; attempt++) {
    // if offline, don't waste time waiting for a timeout
    if (!await options.isOnline()) {
      lastResult = options.makeNetworkErrorResult(null, null);
      break;
    }

    const modelKey = order[attempt];
    let result: TResult;
    try {
      result = await options.scanAsync(modelKey, attempt, order.length);
    } catch (err) {
      options.onNetworkError?.(modelKey, err);
      lastResult = options.makeNetworkErrorResult(modelKey, err);
      // if genuinely offline, all models will fail - stop trying
      if (!await options.isOnline()) break;
      // online but request failed (likely timeout or transient) - try the
      // next model. Don't blacklist: not the model's fault.
      if (attempt < order.length - 1) options.onRetry?.();
      continue;
    }
    if (options.isAborted?.()) return null;

    // if the server reported a provider-side error, blacklist this model
    // and try the next one
    if (options.isProviderSideError(result)) {
      options.onProviderError?.(modelKey, result);
      options.manager.blacklistModel(modelKey);
      lastResult = result;
      if (attempt < order.length - 1) options.onRetry?.();
      continue;
    }

    // success, or a non-provider failure (bad image, quota...) that
    // retrying another model cannot fix - return it as-is
    return result;
  }

  // all candidates failed - return the last failure so the caller's
  // existing error handling shows the appropriate alert
  return lastResult;
}
